import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from 'store/app/entities/baseQuery';

const initialState = {};

function buildUpdatesQueryString(args) {
  const {
    channelName,
    limit = 50,
    platform,
    runtimeVersion,
    branch,
    enabled,
    q,
    createdAtBefore,
  } = args;
  const p = new URLSearchParams();
  p.set('limit', String(limit));
  if (platform) p.set('platform', platform);
  if (runtimeVersion) p.set('runtimeVersion', runtimeVersion);
  if (branch) p.set('branch', branch);
  if (enabled != null && enabled !== '') p.set('enabled', String(enabled));
  if (q) p.set('q', q);
  if (createdAtBefore) p.set('createdAtBefore', createdAtBefore);
  const qs = p.toString();
  return `v1/ota/admin/channels/${encodeURIComponent(channelName)}/updates?${qs}`;
}

const endpoints = build => ({
  listChannels: build.query({
    query: () => ({ url: 'v1/ota/admin/channels' }),
    transformResponse: response => response.data,
    providesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),

  listUpdates: build.query({
    query: args => ({
      url: buildUpdatesQueryString(args),
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, { channelName }) => [
      { type: 'OtaUpdates', id: channelName },
      { type: 'OtaUpdates', id: 'LIST' },
    ],
  }),

  getUpdate: build.query({
    query: id => ({ url: `v1/ota/admin/updates/${encodeURIComponent(id)}` }),
    transformResponse: response => response.data,
    providesTags: (result, error, id) => [{ type: 'OtaUpdate', id }],
  }),

  listBranches: build.query({
    query: channelName => ({
      url: `v1/ota/admin/channels/${encodeURIComponent(channelName)}/branches`,
    }),
    transformResponse: response => response.data,
    providesTags: (result, error, channelName) => [{ type: 'OtaBranches', id: channelName }],
  }),

  createBranch: build.mutation({
    query: ({ channelName, name }) => ({
      url: `v1/ota/admin/channels/${encodeURIComponent(channelName)}/branches`,
      method: 'POST',
      body: { name },
    }),
    transformResponse: response => response.data,
    invalidatesTags: (result, error, { channelName }) => [
      { type: 'OtaBranches', id: channelName },
      { type: 'OtaChannels', id: 'LIST' },
    ],
  }),

  deleteBranch: build.mutation({
    query: ({ channelName, branchName }) => ({
      url: `v1/ota/admin/channels/${encodeURIComponent(channelName)}/branches/${encodeURIComponent(
        branchName,
      )}`,
      method: 'DELETE',
    }),
    transformResponse: response => response.data,
    invalidatesTags: (result, error, { channelName }) => [
      { type: 'OtaBranches', id: channelName },
      { type: 'OtaChannels', id: 'LIST' },
    ],
  }),

  patchChannel: build.mutation({
    query: ({ name, ...body }) => ({
      url: `v1/ota/admin/channels/${encodeURIComponent(name)}`,
      method: 'PATCH',
      body,
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),

  disableUpdate: build.mutation({
    query: id => ({
      url: `v1/ota/admin/updates/${encodeURIComponent(id)}/disable`,
      method: 'POST',
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [
      { type: 'OtaChannels', id: 'LIST' },
      { type: 'OtaUpdates', id: 'LIST' },
    ],
  }),

  enableUpdate: build.mutation({
    query: id => ({
      url: `v1/ota/admin/updates/${encodeURIComponent(id)}/enable`,
      method: 'POST',
    }),
    transformResponse: response => response.data,
    invalidatesTags: (result, error, id) => [
      { type: 'OtaChannels', id: 'LIST' },
      { type: 'OtaUpdates', id: 'LIST' },
      { type: 'OtaUpdate', id },
    ],
  }),

  generateDeeplink: build.mutation({
    query: id => ({
      url: `v1/ota/admin/updates/${encodeURIComponent(id)}/deeplink`,
      method: 'POST',
    }),
    transformResponse: response => response.data,
  }),

  followBranch: build.mutation({
    query: id => ({
      url: `v1/ota/admin/runtime-lines/${encodeURIComponent(id)}/follow`,
      method: 'POST',
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),

  rollbackEmbedded: build.mutation({
    query: id => ({
      url: `v1/ota/admin/runtime-lines/${encodeURIComponent(id)}/rollback-to-embedded`,
      method: 'POST',
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),

  setRollout: build.mutation({
    query: ({ id, updateId, percent = 100 }) => ({
      url: `v1/ota/admin/runtime-lines/${encodeURIComponent(id)}/rollout`,
      method: 'POST',
      body: { updateId, percent },
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),

  pinUpdate: build.mutation({
    query: ({ id, updateId }) => ({
      url: `v1/ota/admin/runtime-lines/${encodeURIComponent(id)}/pin`,
      method: 'POST',
      body: { updateId },
    }),
    transformResponse: response => response.data,
    invalidatesTags: () => [{ type: 'OtaChannels', id: 'LIST' }],
  }),
});

const OtaSlice = createApi({
  reducerPath: 'otaReducer',
  baseQuery,
  tagTypes: ['OtaChannels', 'OtaUpdates', 'OtaUpdate', 'OtaBranches'],
  initialState,
  keepUnusedDataFor: 30,
  endpoints,
});

export const {
  useListChannelsQuery,
  useListUpdatesQuery,
  useGetUpdateQuery,
  useListBranchesQuery,
  useCreateBranchMutation,
  useDeleteBranchMutation,
  usePatchChannelMutation,
  useDisableUpdateMutation,
  useEnableUpdateMutation,
  useGenerateDeeplinkMutation,
  useFollowBranchMutation,
  useRollbackEmbeddedMutation,
  useSetRolloutMutation,
  usePinUpdateMutation,
} = OtaSlice;

export const { middleware: otaMiddleware, reducer: otaReducer } = OtaSlice;
export default OtaSlice;
